<template>
    <section class="wrap wrap-large py-12">
        <form
            class="pb-[2em] at1024:pb-[4em] px-[2em] at1024:px-[4em] border w-full mx-auto border-slate-300 at1024:my-8"
            @submit.prevent="submitForm"
            :class="{ errors: errors }"
        >
            <header class="text-center pt-[2em]">
                <h1 class="text-[40px] at720:text-4xl my-[30px] at1024:text-[80px]">
                    {{ $t("Registration") }}
                </h1>
            </header>

            <div class="text-center">
                <p class="font-medium text-[28px] at1024:text-[35px] mb-[1.1em]">{{ $t("Billing Information") }}</p>
            </div>

            <div class="flex flex-col gap-3">
                <SeclockInput
                    type="text"
                    label="Billing Address"
                    :animated="true"
                    :contrast="true"
                    id="registration:billing_address"
                    v-model="selected_place.address"
                    maxlength="40"
                    required
                />
                <SeclockInput
                    type="text"
                    label="Billing Address 2"
                    :animated="true"
                    :contrast="true"
                    v-model="selected_place.address_2"
                    maxlength="40"
                />
                <div class="at768:flex-row flex flex-col gap-3">
                    <div class="at768:w-1/3">
                        <SeclockInput
                            type="text"
                            label="City"
                            v-model="selected_place.city"
                            :animated="true"
                            :contrast="true"
                            maxlength="40"
                            required
                        />
                    </div>
                    <div class="at768:w-1/3">
                        <SeclockInput
                            type="select"
                            label="State"
                            v-model="selected_place.state"
                            :animated="true"
                            :contrast="true"
                        >
                            <option v-for="state in config.states" :value="state">{{ state }}</option>
                        </SeclockInput>
                    </div>
                    <div class="at768:w-1/3">
                        <SeclockInput
                            type="tel"
                            label="Zipcode"
                            v-model="selected_place.zip"
                            :animated="true"
                            :contrast="true"
                            required
                            maxlength="10"
                        />
                    </div>
                </div>
                <SeclockInput
                    type="select"
                    label="Country"
                    v-model="selected_place.country"
                    :animated="true"
                    :contrast="true"
                >
                    <option value="US">{{ $t("United States") }}</option>
                    <option value="CAN">{{ $t('Canada') }}</option>
                </SeclockInput>
            </div>

            <div class="text-black">
                <ul>
                    <ValidationError v-for="(error, key) in form.errors" :text="error"/>
                </ul>
            </div>

            <div class="flex justify-between items-center mt-[1em] mb-[2em]">
                <button
                    @click="goBack"
                    type="button"
                    class="uppercase tracking-widest flex gap-1 items-center"
                >
                    <BaseSvg name="icon-chevron-left" />
                    {{ $t("PREV") }}
                </button>
                <button
                    type="submit"
                    :disabled="!isFormValid || form.processing"
                    class="uppercase tracking-widest btn-white-on-red"
                >
                    {{ $t("SUBMIT") }}
                </button>
            </div>
            <RecaptchaDisclaimer class="mt-16 text-center text-gray text-sm" />
        </form>
    </section>
</template>

<script setup>
import {
    BaseSvg,
    RecaptchaDisclaimer,
    SeclockInput,
    ValidationError,
} from "@/Components";
import { useGoogleMapsApi } from "@/Composables";
import config from "../../config";
import { computed, onMounted } from "vue";

const props = defineProps(["errors", "form"]);

const goBack = () => {
    emit("goToNextStep", {
        step: "RegistrationStep2",
    });
};

const submitForm = () => {
    if (isFormValid.value) {
        emit("submitForm", selected_place);
    }
};

const isFormValid = computed(() => {
    // Billing Address validation
    if (!selected_place.address?.trim()) return false;

    // 'Billing Address 2' is optional, no need to validate whether it's filled, but should be checked for length if used
    if (selected_place.address_2 && selected_place.address_2?.length > 40) return false;

    // City validation
    if (!selected_place.city?.trim()) return false;

    // State validation - assuming it's optional as there's no required attribute, but should have a valid selection if used
    if (selected_place.state && !config.states.includes(selected_place.state)) return false;

    // Zipcode validation - should be a non-empty, properly formatted code
    const zipcodePattern = /^[0-9]{5}(-[0-9]{4})?$/; // US zipcode pattern, adjust if other formats are needed
    if (!selected_place.zip?.match(zipcodePattern) && selected_place.country === 'US') return false;

    // Country validation - should be a non-empty, valid country
    if (!selected_place.country || (selected_place.country !== 'US' && selected_place.country !== 'CAN')) return false;

    // All validations pass
    return true;
});

const emit = defineEmits(["goToNextStep", "submitForm"]);

//Google Places - Address autocomplete
onMounted(() => bind_autocomplete("registration:billing_address"));
const { bind_autocomplete, selected_place } = useGoogleMapsApi();
</script>
